import { useMemo, useState } from "react";
import { Checkbox } from "../../../../components/Checkbox";
import Modal from "../../../../components/Modal";
import { SelectInput } from "../../../../components/SelectInput/SelectInput";
import { RadioButton } from "../../../../components/RadioButton";
import { useApp } from "../../../../services/app-context";
import { StatusSelect } from "./StatusSelect";
import { MultiSelectLocationList } from "./MultiSelectLocationList";
import { api } from "../../../../services/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "../../../../components/Button";
import { ShowToast } from "../../../../services/toast";
import { getLabel } from "../../utils/labels";

const exportByOptions = [
    {
        label: "All Assignees",
        value: "all"
    },
    {
        label: "Region",
        value: "region"
    },
    {
        label: "Zone",
        value: "zone"
    },
    {
        label: "Status",
        value: "status"
    }
]

export function ExportCertification({ isOpen, onClose, certification }: { isOpen: boolean, onClose: () => void, certification: any }) {
    const [exportBy, setExportBy] = useState("all");
    const [fileType, setFileType] = useState<string>("csv");
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
    const [selectedZones, setSelectedZones] = useState<string[]>([]);
    const { certificationAssets } = useApp();
    const filterOptions = certificationAssets?.filterOptions || {};
    const statusOptions = filterOptions?.certificationStatus?.map((status: string) => ({
        label: certificationAssets?.userStatus?.[status]?.label,
        value: status
    })) ?? [];
    const labels = certificationAssets?.labels || {};
    const certificationSingular = getLabel("certification.singular", labels);

    const downloadFile = async (url: string, fileType: string, certificationName: string) => {
        try {
            const fileName = `${certificationName.replace(/ /g, '_')}.${fileType}`;
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            if (fileType === 'pdf') {
                a.rel = 'noopener noreferrer';
                a.target = '_blank';
            }
            a.click();
            a.remove();
        } catch (error) {
            ShowToast({
                message: "Failed to download file",
                type: "error",
            })
        }
    }

    const { mutate: exportCertification, isLoading: isExportingCertification } = useMutation({
        mutationFn: (data: any) => api.exportCertification(data),
        onSuccess: async (data: any) => {
            await downloadFile(data.data.downloadURL, fileType, certification.certificationName);
            ShowToast({
                message: `${certificationSingular} exported successfully`,
                type: "success",
            })
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || `Failed to export ${certificationSingular.toLowerCase()}`;
            ShowToast({
                message: errorMessage,
                type: "error",
            })
        },
    });

    const { data: usersData } = useQuery({
        queryKey: ["export-options"],
        queryFn: () => api.searchCertificationsUsers({ search: '' }),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchIntervalInBackground: false,
    });
    const regionsData = usersData?.data?.regions || [];
    const zonesData = usersData?.data?.territories || [];

    const regionsOptions = regionsData?.map((region) => ({
        label: region?.name,
        value: region?.id,
    }));
    const zonesOptions = zonesData?.map((zone) => ({
        label: zone?.name,
        value: zone?.id,
    }));

    const handleExportCertification = () => {
        try {
            const payload: any = {
                certificationID: certification.id,
                fileType: fileType,
            };
            if (exportBy === "status") {
                payload.statuses = selectedStatus ? [selectedStatus] : [];
            }
            if (exportBy === "region") {
                payload.regionIDs = selectedRegions;
            }
            if (exportBy === "zone") {
                payload.territoryIDs = selectedZones;
            }
            exportCertification(payload);
        } catch (error) {
            ShowToast({
                message: `Failed to export ${certificationSingular.toLowerCase()}`,
                type: "error",
            })
        }
    }

    const isDisabled = useMemo(() => {
        if (exportBy === "all") return false;
        if (exportBy === "region") return selectedRegions.length === 0;
        if (exportBy === "zone") return selectedZones.length === 0;
        if (exportBy === "status") return selectedStatus === "";
        return false;
    }, [exportBy, selectedRegions, selectedZones, selectedStatus]);

    return (
        <Modal
            isOpen={isOpen}
            handleModal={onClose}
            contentClassName="!w-[750px] !px-0"
            title="Export"
        >

            <div className="px-5 mb-4">
                <div className="text-text-primary text-[15px] mt-1">
                    Prepare your export file
                </div>
                <div className="border-b border-b-[#E8E8E8] mt-4" />
            </div>

            <div className="px-5 mb-4">
                <div className="text-base font-medium">
                    {getLabel("certificationName.singular", labels)}
                </div>
                <div className="text-text-primary text-[15px] mt-2 font-medium">
                    {certification.certificationName}
                </div>
                <div className="text-xs text-[#939393] leading-[12px] mt-2">
                    Includes: Name, {getLabel("attempt.plural", labels)}, {getLabel("attempt.singular", labels)} {getLabel("status.singular", labels)}, Completion Date
                </div>
                <div className="border-b border-b-[#E8E8E8] mt-4" />
            </div>
            <div className="px-5 mb-4 flex items-stretch justify-between">
                <div className="flex-1">
                    <div className="text-base font-medium">Export by</div>
                    <div className="flex flex-col gap-1">
                        {
                            exportByOptions.map((option) => (
                                <RadioButton
                                    key={option.value}
                                    checked={exportBy === option.value}
                                    onChange={() => setExportBy(option.value)}
                                    label={option.label}
                                    id={option.value}
                                    className="hover:bg-[#F1F1F1] w-[150px] p-2 rounded-[6px]"
                                />
                            ))
                        }

                    </div>
                </div>
                {exportBy !== "all" && (
                    <>
                        <div className="border-r border-r-[#E8E8E8] w-[1px] mx-6" />
                        <>
                            {exportBy === "status" && (<StatusSelect
                                selectedStatus={selectedStatus}
                                setSelectedStatus={setSelectedStatus}
                                statusOptions={statusOptions}
                            />
                            )}
                            {exportBy === "region" && (<MultiSelectLocationList
                                selected={selectedRegions}
                                setSelected={setSelectedRegions}
                                options={regionsOptions}
                                label="Select Region"
                            />
                            )}
                            {exportBy === "zone" && (<MultiSelectLocationList
                                selected={selectedZones}
                                setSelected={setSelectedZones}
                                options={zonesOptions}
                                label="Select Zone"
                            />
                            )}
                        </>
                    </>
                )}
                <div className="border-r border-r-[#E8E8E8] w-[1px] mx-6" />
                <div className="flex-1">
                    <div className="text-base font-medium">File Format</div>
                    <div>
                        <SelectInput
                            options={[
                                {
                                    label: "CSV",
                                    value: "csv"
                                },
                                {
                                    label: "PDF",
                                    value: "pdf"
                                }
                            ]}
                            value={fileType}
                            onChange={(e) => setFileType(e.target.value)}
                            placeholder="Select File Format"
                            className="h-[34px]"
                        />
                    </div>
                </div>

            </div>

            <div className="px-5 mb-4">
                <div className="border-b border-b-[#E8E8E8] mb-4" />
                <div className="text-base font-medium">
                    Export Summary
                </div>
                <div className="flex flex-col gap-2 mt-3 text-sm text-black">
                    <div className="flex gap-1">
                        <span className="font-medium">{getLabel("certificationName.singular", labels)}:</span>
                        <span>{certification.certificationName}</span>
                    </div>
                    <div className="flex gap-1">
                        <span className="font-medium">Export by:</span>
                        <span>{exportByOptions.find((option) => option.value === exportBy)?.label}</span>
                    </div>
                    <div className="flex gap-1">
                        <span className="font-medium">File Format:</span>
                        <span>{fileType.toUpperCase()}</span>
                    </div>
                </div>
            </div>

            <div className="px-5 mb-4">
                <div className="border-b border-b-[#E8E8E8] mb-4" />
                <div className="flex justify-end">
                    <Button
                        variant="primary"
                        onClick={handleExportCertification}
                        className="!w-fit !px-8 !py-4 !rounded-full"
                        disabled={isDisabled || isExportingCertification}
                        loading={isExportingCertification}
                    >
                        Export
                    </Button>
                </div>
            </div>
        </Modal>
    );
}